import { FC } from 'react'
import { useTranslation } from 'react-i18next'

import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import KeyboardArrowRightRoundedIcon from '@mui/icons-material/KeyboardArrowRightRounded'
import { Box, ButtonBase, Paper, Typography, alpha, useTheme } from '@mui/material'

import { borderRadiusDefault } from '../../../../themes/theme'
import { diagnosisScoresSx } from '../../helpers/diagnosisStyle'
import { displayedScoreGap } from '../../helpers/scoreDisplay'
import { useUATaxonomy } from '../../hooks/useUATaxonomy'
import { MotivationDiagnosisCategory, TermRef } from '../../types'
import { DiagnosisGapChip } from '../DiagnosisPrimitives/DiagnosisGapChip'
import { DiagnosisNoticePanel } from '../DiagnosisPrimitives/DiagnosisNoticePanel'
import { MotivationGapBar, gapBarFlowKeyframesSx, gapBarIconsFanApartSx, gapBarRevealOnHoverSx } from '../MotivationGapBar/MotivationGapBar'

/**
 * Buckets a single motivation can fall into when comparing two games. Aliases
 * the single-game category so both diagnostics share one accent lookup.
 */
export type ComparisonCategory = MotivationDiagnosisCategory

export interface ComparisonCard {
  motivation: TermRef
  category: ComparisonCategory
  scores: { source: number; target: number }
  /**
   * i18n key of the explanation rendered under this card, varied by how far
   * the two sides diverge (mirrors the single-game diagnosis insight keys).
   */
  insightKey: string
}

interface UnavailableComparisonDiagnosisCardProps {
  body: string
}

/**
 * Disclaimer surfaced when either side of the comparison has no
 * `gameDesign` motivation profile on file. Visually mirrors the
 * single-game `UnavailableDiagnosisCard` so users see the same vocabulary
 * across both diagnostics.
 */
export const UnavailableComparisonDiagnosisCard: FC<UnavailableComparisonDiagnosisCardProps> = ({ body }) => {
  const { t } = useTranslation('ua-insights')
  const theme = useTheme()

  return (
    <DiagnosisNoticePanel
      accent={theme.palette.warning.main}
      icon={<InfoOutlinedIcon fontSize="small" />}
      title={t('diagnosis_unavailable_title')}
      body={body}
    />
  )
}

interface ComparisonDiagnosisCardProps {
  card: ComparisonCard
  accent: string
  sourceName: string
  targetName: string
  /** Source game app icon, shown at the gap bar's source marker. */
  sourceIcon?: string
  /** Target game/genre app icon, shown at the gap bar's delivered marker. */
  targetIcon?: string
  /** Marks the widest-diverging card so the renderer can headline it. */
  isTopOfBucket: boolean
  onClick: () => void
}

/**
 * One motivation row inside a comparison column. Card layout / hover
 * vocabulary mirrors the single-game `DiagnosisCard`, and so does the body: the
 * same {@link MotivationGapBar} bullet bar on a shared 0–5 track — the source
 * side as the goalpost marker, the target side as the solid coverage fill, and
 * a hatched band for the gap between them. An outlined gap chip (promoted to a
 * filled "Biggest gap" pill on the widest card) and a per-card insight sentence
 * carry the rest of the single-game diagnosis treatment across.
 */
export const ComparisonDiagnosisCard: FC<ComparisonDiagnosisCardProps> = ({
  card,
  accent,
  sourceName,
  targetName,
  sourceIcon,
  targetIcon,
  isTopOfBucket,
  onClick,
}) => {
  const { t } = useTranslation('ua-insights')
  const theme = useTheme()
  const { motivationLabel } = useUATaxonomy()

  // The divergence magnitude drives both the chip value and the sorting, so
  // surface it as a first-class number instead of asking the reader to
  // subtract the two trailing scores. Computed from the displayed (1-decimal)
  // sides so the chip always reconciles with the values shown in the trailing
  // column (e.g. 2.0 / 1.9 → 0.1, never a raw 0.2).
  const gap = Math.abs(displayedScoreGap(card.scores.source, card.scores.target))
  // Whichever side rates the motivation higher "leads"; the other "lags". The
  // per-card sentence is phrased from the leader's point of view.
  const sourceLeads = card.scores.source >= card.scores.target
  const leader = sourceLeads ? sourceName : targetName
  const laggard = sourceLeads ? targetName : sourceName

  return (
    <ButtonBase
      onClick={onClick}
      focusRipple
      sx={{
        textAlign: 'left',
        display: 'block',
        width: '100%',
        borderRadius: borderRadiusDefault,
        ...gapBarFlowKeyframesSx,
        // Hover/focus mirrors the single-game `MotivationDiagnosis` cards.
        '&:hover, &.Mui-focusVisible': {
          '& .diagnosis-card-paper': {
            boxShadow: `0 4px 14px ${alpha(accent, 0.22)}`,
            backgroundColor: alpha(accent, 0.04),
          },
          '& .diagnosis-card-chevron': { opacity: 1, color: accent },
          ...gapBarRevealOnHoverSx,
          // On top of the shared reveal, the two game-icon markers fan apart
          // ("pop to side") so a near-equal (overlapping) pair becomes legible.
          ...gapBarIconsFanApartSx,
          '& .gap-bar-glyph': { boxShadow: 4 },
        },
        '&:active': {
          '& .diagnosis-card-paper': {
            boxShadow: `0 2px 8px ${alpha(accent, 0.18)}`,
          },
        },
      }}
    >
      <Paper
        elevation={0}
        className="diagnosis-card-paper"
        sx={{
          p: 1.5,
          width: '100%',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
          boxSizing: 'border-box',
          backgroundColor: 'background.paper',
          borderRadius: borderRadiusDefault,
          border: '1px solid',
          borderColor: accent,
          // Resting elevation: a soft accent-tinted shadow so each card reads as
          // a raised surface even before it is hovered (matches the single-game
          // `DiagnosisCard`).
          boxShadow: `0 2px 8px -4px ${alpha(accent, 0.28)}`,
          transition: 'box-shadow 0.18s ease, background-color 0.18s ease',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
          <Typography
            variant="subtitle2"
            sx={{
              fontWeight: 700,
              color: 'text.primary',
              lineHeight: 1.3,
              flex: 1,
              minWidth: 0,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {motivationLabel(card.motivation)}
          </Typography>
          <GapChip gap={gap} accent={accent} isTopOfBucket={isTopOfBucket} />
          <KeyboardArrowRightRoundedIcon
            className="diagnosis-card-chevron"
            sx={{
              fontSize: 18,
              color: alpha(accent, 0.55),
              opacity: 0.7,
              transition: 'opacity 0.18s ease, color 0.18s ease',
              flexShrink: 0,
            }}
          />
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <MotivationGapBar
            target={card.scores.source}
            delivered={card.scores.target}
            diverging={card.category === 'blind_spot'}
            targetColor={theme.palette.secondary.main}
            deliveredColor={theme.palette.primary.main}
            targetLabel={sourceName}
            deliveredLabel={targetName}
            targetIcon={sourceIcon}
            showDeliveredMarker
            deliveredIcon={targetIcon}
            deliveredIsGenre={!targetIcon}
          />
          {/* Score readout ordered to match the bar's left→right reading: the
              target fill (left) first, the source goalpost (right) last. */}
          <Box className="gap-bar-scores" sx={diagnosisScoresSx}>
            <Box component="span" sx={{ color: 'primary.main' }}>
              {card.scores.target.toFixed(1)}
            </Box>
            <Box component="span" sx={{ color: 'text.disabled', mx: 0.25 }}>
              /
            </Box>
            <Box component="span" sx={{ color: 'secondary.main' }}>
              {card.scores.source.toFixed(1)}
            </Box>
          </Box>
        </Box>

        <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.8125rem', lineHeight: 1.4 }}>
          {t(card.insightKey, { leader, laggard })}
        </Typography>
      </Paper>
    </ButtonBase>
  )
}

interface GapChipProps {
  /** Divergence magnitude `|source − target|` between the two sides. */
  gap: number
  accent: string
  /** Marks the widest-diverging card so the chip can headline as "biggest gap". */
  isTopOfBucket: boolean
}

/**
 * Small pill quantifying how far the two sides diverge on this motivation.
 * Mirrors the single-game diagnosis chip: an outlined pill in the category
 * accent, promoted to a filled "Biggest gap" variant on the single widest
 * card so the one priority stands out from the outlined crowd. The value is
 * the unsigned divergence magnitude (a peer comparison has no "behind"
 * direction the way the single-game game-vs-creatives gap does).
 */
const GapChip: FC<GapChipProps> = ({ gap, accent, isTopOfBucket }) => {
  const { t } = useTranslation('ua-insights')
  const labelText = isTopOfBucket ? t('diagnosis_top_gap_chip') : t('diagnosis_gap_label')

  return <DiagnosisGapChip label={labelText} value={gap.toFixed(1)} accent={accent} filled={isTopOfBucket} />
}
